import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { clsx } from "clsx";
import {
  AlertTriangle,
  CheckCircle,
  CheckSquare,
  Info,
  RefreshCw,
  XCircle,
} from "lucide-react";
import {
  ackPersistedAlert,
  listPersistedAlerts,
  resolvePersistedAlert,
  type PersistedAlert,
} from "@/api/endpoints/alerts";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";

type StatusFilter = "active" | "acknowledged" | "resolved" | "all";

const SEVERITY_META: Record<
  string,
  { icon: typeof Info; bg: string; text: string; badge: string }
> = {
  critical: {
    icon: XCircle,
    bg: "bg-red-500/10 border-red-500/30",
    text: "text-red-400",
    badge: "bg-red-500/20 text-red-400",
  },
  warning: {
    icon: AlertTriangle,
    bg: "bg-yellow-500/10 border-yellow-500/30",
    text: "text-yellow-400",
    badge: "bg-yellow-500/20 text-yellow-400",
  },
  info: {
    icon: Info,
    bg: "bg-blue-500/10 border-blue-500/30",
    text: "text-blue-400",
    badge: "bg-blue-500/20 text-blue-400",
  },
};

const STATUS_BADGE: Record<PersistedAlert["status"], string> = {
  active: "bg-amber-500/20 text-amber-300",
  acknowledged: "bg-sky-500/20 text-sky-300",
  resolved: "bg-emerald-500/20 text-emerald-300",
};

export function PersistedAlerts() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<StatusFilter>("active");

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["alerts-db", filter],
    queryFn: () =>
      listPersistedAlerts(filter === "all" ? undefined : filter),
    refetchInterval: 15_000,
  });

  const ackMut = useMutation({
    mutationFn: ackPersistedAlert,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts-db"] }),
  });
  const resolveMut = useMutation({
    mutationFn: resolvePersistedAlert,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts-db"] }),
  });

  const alerts = data ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex gap-1">
          {(["active", "acknowledged", "resolved", "all"] as StatusFilter[]).map(
            (s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={clsx(
                  "rounded-full px-3 py-1 text-xs font-medium transition-colors",
                  filter === s
                    ? "bg-blue-600 text-white"
                    : "border border-gray-700 bg-gray-800 text-gray-400 hover:text-gray-200",
                )}
              >
                {s}
              </button>
            ),
          )}
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-1 rounded border border-gray-700 bg-gray-800 px-2 py-1 text-xs text-gray-300 hover:bg-gray-700"
        >
          <RefreshCw
            className={clsx("h-3.5 w-3.5", isFetching && "animate-spin")}
          />
          Refresh
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="md" />
        </div>
      ) : alerts.length === 0 ? (
        <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-8 text-center">
          <CheckCircle className="mx-auto h-8 w-8 text-green-400" />
          <p className="mt-3 text-gray-400">
            No {filter === "all" ? "" : filter + " "}alerts.
          </p>
        </div>
      ) : (
        alerts.map((a) => (
          <AlertCard
            key={a.id}
            alert={a}
            onAck={() => ackMut.mutate(a.id)}
            onResolve={() => resolveMut.mutate(a.id)}
            pending={ackMut.isPending || resolveMut.isPending}
          />
        ))
      )}
    </div>
  );
}

function AlertCard({
  alert,
  onAck,
  onResolve,
  pending,
}: {
  alert: PersistedAlert;
  onAck: () => void;
  onResolve: () => void;
  pending: boolean;
}) {
  const severity = SEVERITY_META[alert.severity] ?? SEVERITY_META.info;
  const Icon = severity.icon;
  const dim = alert.status !== "active";

  return (
    <div
      className={clsx(
        "rounded-xl border p-4 transition-colors",
        dim ? "border-gray-800 bg-gray-900/30 opacity-70" : severity.bg,
      )}
    >
      <div className="flex items-start gap-3">
        <Icon className={clsx("mt-0.5 h-5 w-5 flex-shrink-0", severity.text)} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="font-medium text-gray-200">{alert.title}</h4>
            <span
              className={clsx(
                "rounded-full px-2 py-0.5 text-xs font-medium",
                severity.badge,
              )}
            >
              {alert.severity}
            </span>
            <span
              className={clsx(
                "rounded-full px-2 py-0.5 text-xs font-medium",
                STATUS_BADGE[alert.status],
              )}
            >
              {alert.status}
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-400">{alert.message}</p>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
            {alert.source && <span>Source: {alert.source}</span>}
            {alert.metric_value !== null && (
              <span>Value: {alert.metric_value.toFixed(2)}</span>
            )}
            <span>Opened {new Date(alert.created_at).toLocaleString()}</span>
            {alert.acknowledged_at && (
              <span>
                Ack'd by {alert.acknowledged_by ?? "?"}{" "}
                {new Date(alert.acknowledged_at).toLocaleString()}
              </span>
            )}
            {alert.resolved_at && (
              <span>
                Resolved {new Date(alert.resolved_at).toLocaleString()}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-shrink-0 flex-col gap-1">
          {alert.status === "active" && (
            <button
              onClick={onAck}
              disabled={pending}
              className="flex items-center gap-1 rounded border border-gray-700 bg-gray-800 px-2 py-1 text-xs text-gray-200 hover:bg-gray-700 disabled:opacity-50"
            >
              <CheckSquare className="h-3 w-3" /> Ack
            </button>
          )}
          {alert.status !== "resolved" && (
            <button
              onClick={onResolve}
              disabled={pending}
              className="flex items-center gap-1 rounded border border-emerald-700/50 bg-emerald-600/10 px-2 py-1 text-xs text-emerald-300 hover:bg-emerald-600/20 disabled:opacity-50"
            >
              <CheckCircle className="h-3 w-3" /> Resolve
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
